<template>
  <v-card>
    <v-toolbar flat color="primary" dark class="header">
      <v-toolbar-title>{{ $t('common.concepts.settings') }}</v-toolbar-title>
      <v-btn class="mr-4" icon variant="flat" size="x-small" @click="$emit('close')">
        <v-icon class="close-icon">mdi-close</v-icon>
        <v-tooltip activator="parent" location="start">{{ $t('common.actions.close') }}</v-tooltip>
      </v-btn>
    </v-toolbar>
    <v-container>
      <v-row>
        <v-col>
          <v-form>
            <v-select
              v-model="tempTheme"
              :items="themeOptions"
              :label="$t('domain.fields.theme')"
              item-title="text"
              item-value="value"
              outlined />
            <v-select
              v-model="tempLang"
              :items="langOptions"
              :label="$t('domain.fields.language')"
              item-title="text"
              item-value="value"
              outlined></v-select>
            <v-select
              :model-value="tenantDateFormat ?? tempDateFormat"
              :items="dateFormats"
              :disabled="!!tenantDateFormat"
              :label="$t('domain.fields.dateFormat')"
              item-title="text"
              item-value="value"
              outlined
              @update:model-value="tempDateFormat = $event"></v-select>
            <v-select
              v-model="tempTimezone"
              :items="timezones"
              :label="$t('domain.fields.timezone')"
              item-title="text"
              item-value="value"
              outlined
              return-object></v-select>
            <!--Hidden for now, waiting for evaluation of this feature-->
            <!--<v-select
              v-model="tempMetricUnits"
              :items="unitOptions"
              item-title="text"
              item-value="value"
              :label="$t('domain.fields.units')"
              outlined
            ></v-select>
            <v-select
              v-model="tempCoordinateUnits"
              :items="coordinateSystemOptions"
              :label="$t('domain.fields.coordinateSystem')"
              item-title="text"
              item-value="value"
              outlined
            ></v-select>-->
            <div class="text-right">
              <v-btn color="primary" @click="saveSettings">{{ $t('common.actions.save') }}</v-btn>
            </div>
          </v-form>
        </v-col>
      </v-row>
    </v-container>
  </v-card>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'

import { storeToRefs } from 'pinia'

import { useI18n } from 'vue-i18n'

import { useNotifications } from '@tsyl/zapan2'

import timezones from '@/constants/timezones.json'
import { updateUserAttributes } from '@/services/api/user.ts'
import AuthStore from '@/stores/auth'
import settingsStore from '@/stores/settingsStore'
import type { UserMetadata } from '@/types/User.ts'

const auth = AuthStore()
const { user, isAuthenticated } = storeToRefs(auth)
const { locale, t } = useI18n()

const { addNotification } = useNotifications()
const settings = settingsStore()
const { lang, theme, dateFormat, tenantDateFormat, timezone, metricUnits, coordinateUnits } = storeToRefs(settings)
const tempLang = ref(lang.value)
const tempTheme = ref(theme.value)
const tempDateFormat = ref(dateFormat.value)
const tempTimezone = computed({
  get() {
    return timezones.find((tz) => tz.utc.includes(timezone.value))
  },
  set(value) {
    timezone.value = value?.utc[0] ?? timezone.value
  }
})
const tempMetricUnits = ref(metricUnits.value)
const tempCoordinateUnits = ref(coordinateUnits.value)

const emit = defineEmits<{
  (e: 'close', v: void): void
}>()

const themeOptions = computed(() => [
  { text: t('domain.enums.theme.light'), value: 'light' },
  { text: t('domain.enums.theme.dark'), value: 'dark' }
])

const langOptions = computed(() => [
  { text: t('domain.enums.language.english'), value: 'en-US' },
  { text: t('domain.enums.language.spanish'), value: 'es-ES' },
  { text: t('domain.enums.language.french'), value: 'fr-FR' },
  { text: t('domain.enums.language.portuguese'), value: 'pt-BR' }
])

const dateFormats = ref([
  { text: 'dd/MM/yy', value: 'dd/MM/yy' },
  { text: 'dd/MM/yyyy', value: 'dd/MM/yyyy' },
  { text: 'MM/dd/yy', value: 'MM/dd/yy' },
  { text: 'MM/dd/yyyy', value: 'MM/dd/yyyy' },
  { text: 'yy/MM/dd', value: 'yy/MM/dd' },
  { text: 'yyyy/MM/dd', value: 'yyyy/MM/dd' }
])

/**
 * Save application settings. They're always saved on local storage but Auth0 is updated if user is logged
 */
const saveSettings = () => {
  const attributes: UserMetadata = {
    Language: tempLang.value,
    Units: tempMetricUnits.value,
    DateFormat: tempDateFormat.value,
    Timezone: timezone.value,
    Coordinates: tempCoordinateUnits.value,
    Theme: tempTheme.value
  }

  // update store and localstorage
  settings.updateSettings(attributes)

  // update language
  locale.value = tempLang.value

  // update Auth0 on background
  if (isAuthenticated.value) {
    updateUserAuth0Settings(attributes)
  }

  emit('close')
}

async function updateUserAuth0Settings(attributes: UserMetadata) {
  try {
    // auth.updateAttributes(attributes)
    if (!user.value) throw new Error('Cannot update settings without an authenticated user')
    user.value.UserMetadata = { ...attributes }
    await updateUserAttributes(user.value)
    addNotification({
      type: 'success',
      title: t('messages.success.userConfigUpdated'),
      time: 10000
    })
  } catch (error) {
    console.error('Update user attributes failed', error)
    addNotification({
      type: 'error',
      title: t('messages.error.userConfigUpdate'),
      time: 10000
    })
    console.error(error)
  }
}
</script>

<style lang="scss" scoped>
.v-card {
  width: 500px;
  margin: 0 auto;
}
.close-icon {
  color: rgb(var(--v-theme-surface-variant)) !important;
}
</style>
